import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { decodeMimeWords, senderLabel } from "@/lib/mime";
import EmailHtmlView from "@/components/EmailHtmlView";
import {
  LogOut, Mail, User, RefreshCw, Clock, Inbox, Shield,
  ChevronLeft, Eye, Copy, Lock, Check, Settings,
  MessageSquare, EyeOff, Save, Camera, Trash2, Search,
  ChevronRight, Paperclip, Ticket, Plus, Send, Reply,
  CheckSquare, Square, MailOpen, X, Pin, ChevronDown, Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import UserTwoFASection from "@/components/user/UserTwoFASection";
import UserPasskeysSection from "@/components/user/UserPasskeysSection";
import UserTicketsSection from "@/components/user/UserTicketsSection";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins}min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function getInitials(name) {
  if (!name) return "?";
  return name.slice(0, 2).toUpperCase();
}

export default function UserDashboard({ onLogout }) {
  const [profile, setProfile] = useState(null);
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("emails");
  const [messageSearch, setMessageSearch] = useState("");
  const [siteSettings, setSiteSettings] = useState(null);
  const [selectedMsgIds, setSelectedMsgIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedEmailIds, setSelectedEmailIds] = useState(new Set());
  const [emailSelectionMode, setEmailSelectionMode] = useState(false);

  // Create email
  const [showCreateEmail, setShowCreateEmail] = useState(false);
  const [newEmailAddress, setNewEmailAddress] = useState("");
  const [newEmailLifetime, setNewEmailLifetime] = useState("");
  const [durationOpen, setDurationOpen] = useState(false);
  const [isCreatingEmail, setIsCreatingEmail] = useState(false);

  // Reply
  const [showReply, setShowReply] = useState(false);
  const [replyTo, setReplyTo] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [replyFiles, setReplyFiles] = useState([]);
  const replyFileRef = useRef(null);

  // Avatar
  const [avatar, setAvatar] = useState(null);
  const fileInputRef = useRef(null);
  const ticketsRef = useRef(null);

  // Password change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [disabledReason, setDisabledReason] = useState(null);
  const [showDisabledDialog, setShowDisabledDialog] = useState(false);

  const token = localStorage.getItem("user_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Periodic account status check (every 10s)
  useEffect(() => {
    const checkStatus = async () => {
      try {
        await axios.get(`${API}/user/status`, { headers: { Authorization: `Bearer ${token}` } });
      } catch (error) {
        if (error.response?.status === 403) {
          const reason = error.response?.data?.detail || "Votre compte a été désactivé par un administrateur.";
          setDisabledReason(reason);
          setShowDisabledDialog(true);
        } else if (error.response?.status === 401) {
          onLogout();
        }
      }
    };
    checkStatus();
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, [token, onLogout]);

  const handleDisabledLogout = () => {
    setShowDisabledDialog(false);
    onLogout();
  };


  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/profile`, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(res.data);
      setAvatar(res.data.avatar || null);
    } catch (error) {
      if (error.response?.status === 401) onLogout();
    }
  }, [token, onLogout]);

  const fetchEmails = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/user/emails`, { headers: { Authorization: `Bearer ${token}` } });
      setEmails(res.data);
    } catch (error) {
      if (error.response?.status === 401) onLogout();
    } finally {
      setIsLoading(false);
    }
  }, [token, onLogout]);

  const fetchMessages = useCallback(async (emailId, silent = false) => {
    if (!silent) setIsLoadingMessages(true);
    try {
      const res = await axios.get(`${API}/user/emails/${emailId}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      // Le polling silencieux ne remplace la liste que si elle a réellement
      // changé, sinon tous les messages se re-rendent toutes les 15 s pour rien.
      setMessages((prev) => {
        const data = res.data;
        const same =
          prev.length === data.length &&
          data.every((m, i) => {
            const p = prev[i];
            return p && p.id === m.id && p.is_read === m.is_read;
          });
        return same ? prev : data;
      });
    } catch (error) {
      if (!silent) toast.error("Erreur lors du chargement des messages");
    } finally {
      if (!silent) setIsLoadingMessages(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
    fetchEmails();
    axios.get(`${API}/settings`).then(res => setSiteSettings(res.data)).catch(() => {});
  }, [fetchProfile, fetchEmails]);

  useEffect(() => {
    if (!selectedEmail) return;
    fetchMessages(selectedEmail.id);
    const interval = setInterval(() => fetchMessages(selectedEmail.id, true), 15000);
    return () => clearInterval(interval);
  }, [selectedEmail, fetchMessages]);

  const openEmail = (email) => {
    setSelectedEmail(email);
    setMessages([]);
    setMessageSearch("");
    exitSelectionMode();
  };

  const openMessage = async (msg) => {
    setSelectedMessage(msg);
    setIsMessageOpen(true);
    if (!msg.is_read) {
      try {
        await axios.post(`${API}/messages/mark-read`, {
          message_ids: [msg.id],
          is_read: true,
        });
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
        if (selectedEmail) {
          setEmails(prev => prev.map(e => e.id === selectedEmail.id
            ? { ...e, unread_count: Math.max(0, (e.unread_count || 0) - 1) }
            : e
          ));
        }
      } catch { /* silent */ }
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.success("Adresse copiée !");
  };

  // Avatar
  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500000) {
      toast.error("Image trop volumineuse (max 500KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.post(`${API}/user/avatar`, { avatar: reader.result }, { headers });
        setAvatar(reader.result);
        toast.success("Avatar mis à jour");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const deleteAvatar = async () => {
    try {
      await axios.delete(`${API}/user/avatar`, { headers });
      setAvatar(null);
      toast.success("Avatar supprimé");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) return;
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setIsSaving(true);
    try {
      await axios.post(`${API}/user/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { headers });
      toast.success("Mot de passe modifié");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setIsSaving(false);
    }
  };

  const togglePinEmail = async (emailId, currentPinned) => {
    try {
      await axios.patch(`${API}/user/emails/${emailId}/pin`, { pinned: !currentPinned }, { headers });
      setEmails(prev => prev.map(e => e.id === emailId ? { ...e, pinned: !currentPinned } : e));
      toast.success(!currentPinned ? "Email épinglé" : "Email désépinglé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const toggleEmailSelection = (emailId) => {
    setSelectedEmailIds(prev => {
      const next = new Set(prev);
      if (next.has(emailId)) next.delete(emailId); else next.add(emailId);
      return next;
    });
  };

  const selectAllEmails = () => {
    if (selectedEmailIds.size === emails.length) {
      setSelectedEmailIds(new Set());
    } else {
      setSelectedEmailIds(new Set(emails.map(e => e.id)));
    }
  };

  const exitEmailSelectionMode = () => {
    setEmailSelectionMode(false);
    setSelectedEmailIds(new Set());
  };

  const handleBulkDeleteEmails = async () => {
    const ids = [...selectedEmailIds];
    if (!ids.length) return;
    try {
      await axios.post(`${API}/user/emails/delete`, { email_ids: ids }, { headers });
      setEmails(prev => prev.filter(e => !ids.includes(e.id)));
      if (selectedEmail && ids.includes(selectedEmail.id)) {
        setSelectedEmail(null);
      }
      toast.success(`${ids.length} email${ids.length > 1 ? "s" : ""} supprimé${ids.length > 1 ? "s" : ""}`);
      exitEmailSelectionMode();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleCreateEmail = async () => {
    setIsCreatingEmail(true);
    try {
      const body = {};
      if (newEmailAddress.trim()) body.custom_address = newEmailAddress.trim();
      if (newEmailLifetime) body.lifetime = newEmailLifetime;
      await axios.post(`${API}/user/emails/generate`, body, { headers });
      toast.success("Email créé !");
      setNewEmailAddress("");
      setNewEmailLifetime("");
      setShowCreateEmail(false);
      fetchEmails();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setIsCreatingEmail(false);
    }
  };

  const handleReply = async () => {
    if (!replyTo.trim() || !replySubject.trim() || !replyBody.trim()) return;
    setIsSendingReply(true);
    try {
      if (replyFiles.length > 0) {
        const formData = new FormData();
        formData.append("to", replyTo.trim());
        formData.append("subject", replySubject.trim());
        formData.append("body", replyBody.trim());
        if (selectedMessage?.id) formData.append("message_id", selectedMessage.id);
        replyFiles.forEach(f => formData.append("files", f));
        await axios.post(`${API}/user/emails/${selectedEmail.id}/reply-with-attachments`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${API}/user/emails/${selectedEmail.id}/reply`, {
          to: replyTo.trim(),
          subject: replySubject.trim(),
          body: replyBody.trim(),
          message_id: selectedMessage?.id,
        }, { headers });
      }
      toast.success("Réponse envoyée !");
      setShowReply(false);
      setReplyTo("");
      setReplySubject("");
      setReplyBody("");
      setReplyFiles([]);
      if (selectedEmail) fetchMessages(selectedEmail.id, true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setIsSendingReply(false);
    }
  };

  const toggleMsgSelection = (msgId) => {
    setSelectedMsgIds(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId); else next.add(msgId);
      return next;
    });
  };

  const selectAllMessages = () => {
    if (selectedMsgIds.size === filteredMessages.length) {
      setSelectedMsgIds(new Set());
    } else {
      setSelectedMsgIds(new Set(filteredMessages.map(m => m.id)));
    }
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedMsgIds(new Set());
  };

  const handleBulkMarkRead = async (isRead) => {
    const ids = [...selectedMsgIds];
    if (!ids.length) return;
    try {
      await axios.post(`${API}/user/messages/mark-read`, { message_ids: ids, is_read: isRead }, { headers });
      // Count how many messages actually changed state
      const changed = messages.filter(m => ids.includes(m.id) && m.is_read !== isRead).length;
      setMessages(prev => prev.map(m => ids.includes(m.id) ? { ...m, is_read: isRead } : m));
      if (selectedEmail && changed > 0) {
        setEmails(prev => prev.map(e => e.id === selectedEmail.id
          ? { ...e, unread_count: Math.max(0, (e.unread_count || 0) + (isRead ? -changed : changed)) }
          : e
        ));
      }
      toast.success(isRead ? "Marqué(s) comme lu(s)" : "Marqué(s) comme non lu(s)");
      exitSelectionMode();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedMsgIds];
    if (!ids.length) return;
    try {
      await axios.post(`${API}/user/messages/delete`, { message_ids: ids }, { headers });
      const deletedUnread = messages.filter(m => ids.includes(m.id) && !m.is_read).length;
      setMessages(prev => prev.filter(m => !ids.includes(m.id)));
      if (selectedEmail) {
        setEmails(prev => prev.map(e => e.id === selectedEmail.id
          ? {
              ...e,
              message_count: Math.max(0, (e.message_count || 0) - ids.length),
              unread_count: Math.max(0, (e.unread_count || 0) - deletedUnread),
            }
          : e
        ));
      }
      toast.success(`${ids.length} message${ids.length > 1 ? "s" : ""} supprimé${ids.length > 1 ? "s" : ""}`);
      exitSelectionMode();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const formatRemaining = (seconds) => {
    if (seconds === -1) return null;
    if (seconds <= 0) return "Expiré";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  // Stats
  const totalEmails = emails.length;
  const totalMessages = emails.reduce((sum, e) => sum + (e.message_count || 0), 0);
  const totalUnread = emails.reduce((sum, e) => sum + (e.unread_count || 0), 0);
  const permanentEmails = emails.filter(e => e.is_permanent).length;

  // Filter messages
  const filteredMessages = messages.filter(msg => {
    if (!messageSearch.trim()) return true;
    const q = messageSearch.toLowerCase();
    return decodeMimeWords(msg.subject || "").toLowerCase().includes(q) ||
           senderLabel(msg).toLowerCase().includes(q) ||
           (msg.sender || "").toLowerCase().includes(q) ||
           (msg.body || "").toLowerCase().includes(q);
  });

  const tabs = [
    { id: "emails", label: "Mes emails", icon: Mail, badge: totalUnread > 0 ? totalUnread : null },
    { id: "tickets", label: "Tickets", icon: Ticket },
    { id: "account", label: "Mon compte", icon: Settings },
  ];

  // Render avatar (header or account)
  const renderAvatar = (size = "sm") => {
    const sizeClasses = size === "lg"
      ? "w-16 h-16 text-xl border-2"
      : "w-9 h-9 text-xs border";
    const iconSize = size === "lg" ? "w-8 h-8" : "w-4 h-4";

    if (avatar) {
      return (
        <img
          src={avatar}
          alt="Avatar"
          className={`${sizeClasses} rounded-full object-cover border-cyan-500/30`}
        />
      );
    }
    return (
      <div className={`${sizeClasses} rounded-full bg-gradient-to-br from-cyan-500/30 to-blue-500/30 flex items-center justify-center border-cyan-500/20 font-mono font-bold text-cyan-400`}>
        {getInitials(profile?.username)}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-animated flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/95 backdrop-blur-md sticky top-0 z-30" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {selectedEmail ? (
              <>
                <button onClick={() => setSelectedEmail(null)} className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800 transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="font-mono text-sm font-bold text-white truncate max-w-[200px] sm:max-w-none">
                    {selectedEmail.full_address}
                  </h1>
                  <p className="text-[10px] text-zinc-500 flex items-center gap-1.5">
                    <span className="text-cyan-400/60">{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
                    <span className="text-zinc-700">•</span>
                    <span className={selectedEmail.is_permanent ? "text-emerald-400/60" : selectedEmail.is_expired ? "text-red-400/60" : "text-zinc-500"}>
                      {selectedEmail.is_permanent ? "Permanent" : formatRemaining(selectedEmail.remaining_seconds) || ""}
                    </span>
                  </p>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3 cursor-pointer group/user" onClick={() => window.location.reload()}>
                {renderAvatar("sm")}
                <div>
                  <h1 className="font-mono text-sm font-bold text-white group-hover/user:text-cyan-400 transition-colors duration-200">{profile?.username || "..."}</h1>
                  <p className="text-[10px] text-zinc-500">Mon espace</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              onClick={() => {
                if (selectedEmail) {
                  fetchMessages(selectedEmail.id);
                } else if (activeTab === "emails") {
                  fetchEmails();
                } else if (activeTab === "tickets") {
                  ticketsRef.current?.refresh();
                } else if (activeTab === "account") {
                  fetchProfile();
                }
                setIsRefreshing(true);
                setTimeout(() => setIsRefreshing(false), 1500);
              }}
              variant="ghost"
              size="sm"
              className="text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 h-8 w-8 p-0 group/refresh"
            >
              <RefreshCw className={`w-4 h-4 transition-transform duration-500 ${isRefreshing || isLoading || isLoadingMessages ? "animate-spin" : "group-hover/refresh:rotate-[360deg]"}`} />
            </Button>
            <Button
              onClick={onLogout}
              variant="ghost"
              size="sm"
              className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0 group/logout"
            >
              <LogOut className="w-4 h-4 transition-transform duration-300 group-hover/logout:translate-x-0.5" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        {!selectedEmail && (
          <div className="max-w-5xl mx-auto px-4">
            <div className="flex gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group/tab flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium transition-colors duration-150 relative ${
                    activeTab === tab.id
                      ? "text-cyan-400"
                      : "text-zinc-500 hover:text-cyan-400 hover:bg-cyan-500/10 hover:translate-y-[-1px]"
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5 transition-transform duration-200 group-hover/tab:scale-110 group-hover/tab:rotate-6" />
                  {tab.label}
                  {tab.badge && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-[9px] font-mono">
                      {tab.badge}
                    </span>
                  )}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-4 sm:py-6">
        {selectedEmail ? (
          /* ===== INBOX VIEW ===== */
          <div className="space-y-4">
            {/* Email info card */}
            <div className={`relative overflow-hidden rounded-xl p-4 flex items-center gap-3 border ${
              selectedEmail.is_permanent ? "bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-zinc-900/50 border-emerald-500/20" :
              selectedEmail.is_expired ? "bg-gradient-to-r from-red-500/10 via-red-500/5 to-zinc-900/50 border-red-500/20" :
              "bg-gradient-to-r from-cyan-500/10 via-cyan-500/5 to-zinc-900/50 border-cyan-500/20"
            }`}>
              <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
                selectedEmail.is_permanent ? "bg-emerald-500/20 border border-emerald-500/30 shadow-lg shadow-emerald-500/10" :
                selectedEmail.is_expired ? "bg-red-500/20 border border-red-500/30 shadow-lg shadow-red-500/10" :
                "bg-cyan-500/20 border border-cyan-500/30 shadow-lg shadow-cyan-500/10"
              }`}>
                <Mail className={`w-5 h-5 ${
                  selectedEmail.is_permanent ? "text-emerald-400" :
                  selectedEmail.is_expired ? "text-red-400" :
                  "text-cyan-400"
                }`} />
              </div>
              <div className="flex-1 min-w-0">
                <p onClick={() => copyAddress(selectedEmail.full_address)} className="text-sm text-white font-mono font-medium truncate cursor-pointer hover:text-cyan-400 transition-colors" title="Cliquer pour copier">{selectedEmail.full_address}</p>
                <div className="flex items-center gap-2 mt-1">
                  {selectedEmail.is_permanent && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[9px]">Permanent</Badge>
                  )}
                  {selectedEmail.is_expired && (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[9px]">Expiré</Badge>
                  )}
                  {!selectedEmail.is_permanent && !selectedEmail.is_expired && (
                    <Badge className="bg-cyan-500/15 text-cyan-400 border-cyan-500/25 text-[9px]">
                      <Clock className="w-2.5 h-2.5 mr-0.5" /> {formatRemaining(selectedEmail.remaining_seconds)}
                    </Badge>
                  )}
                  <span className="text-[10px] text-zinc-500">{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
                </div>
              </div>
              <button
                onClick={() => copyAddress(selectedEmail.full_address)}
                className={`p-2.5 rounded-lg transition-colors ${
                  selectedEmail.is_permanent ? "text-emerald-400/60 hover:text-emerald-400 hover:bg-emerald-500/10" :
                  selectedEmail.is_expired ? "text-red-400/60 hover:text-red-400 hover:bg-red-500/10" :
                  "text-cyan-400/60 hover:text-cyan-400 hover:bg-cyan-500/10"
                }`}
                title="Copier l'adresse"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>

            {/* Search + selection toolbar */}
            {messages.length > 0 && (
              <div className="flex items-center gap-2">
                {selectionMode ? (
                  <div className="flex items-center gap-2 w-full bg-cyan-500/5 border border-cyan-500/20 rounded-lg px-3 py-2">
                    <button onClick={selectAllMessages} className="flex items-center gap-2 text-cyan-400/60 hover:text-cyan-400 p-0.5 flex-1 cursor-pointer" title={selectedMsgIds.size === filteredMessages.length ? "Tout désélectionner" : "Tout sélectionner"}>
                      {selectedMsgIds.size === filteredMessages.length && filteredMessages.length > 0
                        ? <CheckSquare className="w-4 h-4 text-cyan-400" />
                        : <Square className="w-4 h-4" />}
                      <span className="text-xs text-cyan-400/80">
                        {selectedMsgIds.size} sélectionné{selectedMsgIds.size > 1 ? "s" : ""}
                      </span>
                    </button>
                    <button
                      onClick={() => handleBulkMarkRead(true)}
                      disabled={selectedMsgIds.size === 0}
                      className="text-cyan-400/60 hover:text-cyan-400 disabled:opacity-30 p-1.5 rounded-md hover:bg-cyan-500/10 transition-colors"
                      title="Marquer comme lu"
                    >
                      <MailOpen className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleBulkMarkRead(false)}
                      disabled={selectedMsgIds.size === 0}
                      className="text-amber-400/60 hover:text-amber-400 disabled:opacity-30 p-1.5 rounded-md hover:bg-amber-500/10 transition-colors"
                      title="Marquer comme non lu"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      disabled={selectedMsgIds.size === 0}
                      className="text-red-400/60 hover:text-red-400 disabled:opacity-30 p-1.5 rounded-md hover:bg-red-500/10 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={exitSelectionMode}
                      className="text-zinc-400 hover:text-white p-1.5 rounded-md hover:bg-zinc-800 transition-colors"
                      title="Annuler"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    {messages.length > 3 && (
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input
                          placeholder="Rechercher dans les messages..."
                          value={messageSearch}
                          onChange={(e) => setMessageSearch(e.target.value)}
                          className="pl-9 bg-zinc-900 border-zinc-800 text-white text-sm h-9"
                        />
                      </div>
                    )}
                    <Button
                      onClick={() => setSelectionMode(true)}
                      variant="ghost"
                      size="sm"
                      className="text-zinc-500 hover:text-white h-9 px-2.5 shrink-0"
                      title="Sélectionner des messages"
                    >
                      <CheckSquare className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Messages list */}
            {isLoadingMessages && messages.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-16 space-y-3">
                <div className="w-14 h-14 rounded-full bg-zinc-900 flex items-center justify-center mx-auto border border-zinc-800">
                  <Inbox className="w-7 h-7 text-zinc-700" />
                </div>
                <p className="text-zinc-500 text-sm">Aucun message reçu</p>
                <p className="text-zinc-600 text-xs">Les nouveaux messages apparaîtront ici</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {filteredMessages.map((msg) => {
                  const isSentMsg = msg.direction === "sent";
                  return (
                  <div
                    key={msg.id}
                    onClick={() => selectionMode ? toggleMsgSelection(msg.id) : openMessage(msg)}
                    className={`w-full text-left border rounded-xl p-3.5 sm:p-4 transition-colors group cursor-pointer ${
                      selectedMsgIds.has(msg.id) ? "border-cyan-500/40 bg-cyan-500/5" :
                      isSentMsg ? "bg-gradient-to-r from-emerald-500/[0.04] to-transparent border-emerald-500/20 hover:border-emerald-500/40 hover:from-emerald-500/[0.08]" :
                      msg.is_read ? "bg-zinc-900/50 border-zinc-800/50 hover:border-zinc-700 hover:bg-zinc-900/80" :
                      "bg-gradient-to-r from-cyan-500/[0.04] to-transparent border-cyan-500/20 hover:border-cyan-500/40 hover:from-cyan-500/[0.08]"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {selectionMode ? (
                        <div className="w-9 h-9 flex items-center justify-center shrink-0">
                          {selectedMsgIds.has(msg.id)
                            ? <CheckSquare className="w-5 h-5 text-cyan-400" />
                            : <Square className="w-5 h-5 text-zinc-600" />}
                        </div>
                      ) : isSentMsg ? (
                        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 shadow-sm shadow-emerald-500/10">
                          <Send className="w-4 h-4" />
                        </div>
                      ) : (
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold font-mono ${
                          msg.is_read ? "bg-zinc-800/80 text-zinc-500" : "bg-cyan-500/15 text-cyan-400 border border-cyan-500/25 shadow-sm shadow-cyan-500/10"
                        }`}>
                          {getInitials(msg.sender?.split("@")[0] || msg.sender)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {isSentMsg ? (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-mono shrink-0">Envoyé</span>
                            ) : !msg.is_read && !selectionMode ? (
                              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shrink-0" />
                            ) : null}
                            <p className={`text-sm truncate flex items-center gap-1 ${isSentMsg ? "text-emerald-400" : msg.is_read ? "text-zinc-400" : "text-white font-medium"}`}>
                              {msg.attachments?.length > 0 && <Paperclip className="w-3 h-3 text-cyan-400/60 shrink-0" />}
                              {decodeMimeWords(msg.subject) || "(sans sujet)"}
                            </p>
                          </div>
                          <span className={`text-[10px] font-mono shrink-0 whitespace-nowrap ${isSentMsg ? "text-emerald-500/60" : msg.is_read ? "text-zinc-600" : "text-cyan-500/60"}`}>
                            {formatRelativeTime(msg.received_at)}
                          </span>
                        </div>
                        <p className={`text-xs mt-0.5 truncate ${isSentMsg ? "text-emerald-400/60" : msg.is_read ? "text-zinc-500" : "text-cyan-400/60"}`}>
                          {isSentMsg ? `→ ${msg.recipient || msg.sender}` : senderLabel(msg)}
                        </p>
                        {msg.body && (
                          <p className="text-[11px] text-zinc-500 mt-1 truncate leading-relaxed">
                            {msg.body.replace(/<[^>]*>/g, "").slice(0, 120)}
                          </p>
                        )}
                      </div>
                      {!selectionMode && <ChevronRight className={`w-4 h-4 transition-colors shrink-0 mt-1 ${isSentMsg ? "text-emerald-500/30 group-hover:text-emerald-400" : msg.is_read ? "text-zinc-800 group-hover:text-zinc-500" : "text-cyan-500/30 group-hover:text-cyan-400"}`} />}
                    </div>
                  </div>
                  );
                })}
                {filteredMessages.length === 0 && messageSearch && (
                  <div className="text-center py-8 text-zinc-600 text-xs">
                    Aucun message correspondant à "{messageSearch}"
                  </div>
                )}
              </div>
            )}
          </div>
        ) : activeTab === "emails" ? (
          /* ===== EMAILS TAB ===== */
          <div className="space-y-5">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="group/stat bg-zinc-900/40 backdrop-blur-sm border border-zinc-800/50 border-cyan-500/10 p-3 sm:p-4 rounded-xl relative overflow-hidden hover:border-zinc-700/50 transition-colors duration-150" style={{ boxShadow: "0 0 20px rgba(34,211,238,0.06)" }}>
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyan-500/50 to-transparent opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover/stat:opacity-100 transition-opacity duration-300" />
                <div className="flex items-center gap-1.5 text-cyan-400/60 mb-1.5 relative">
                  <Mail className="w-3.5 h-3.5" />
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-wider font-medium">Emails</span>
                </div>
                <p className="text-lg sm:text-2xl font-mono font-bold text-white relative tracking-tight">{totalEmails}</p>
              </div>
              <div className="group/stat bg-zinc-900/40 backdrop-blur-sm border border-zinc-800/50 border-blue-500/10 p-3 sm:p-4 rounded-xl relative overflow-hidden hover:border-zinc-700/50 transition-colors duration-150" style={{ boxShadow: "0 0 20px rgba(96,165,250,0.06)" }}>
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-blue-500/50 to-transparent opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover/stat:opacity-100 transition-opacity duration-300" />
                <div className="flex items-center gap-1.5 text-blue-400/60 mb-1.5 relative">
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-wider font-medium">Messages</span>
                </div>
                <p className="text-lg sm:text-2xl font-mono font-bold text-cyan-400 relative tracking-tight">{totalMessages}</p>
              </div>
              <div className={`group/stat bg-zinc-900/40 backdrop-blur-sm border border-zinc-800/50 p-3 sm:p-4 rounded-xl relative overflow-hidden hover:border-zinc-700/50 transition-colors duration-150 ${totalUnread > 0 ? "border-amber-500/10" : ""}`} style={{ boxShadow: totalUnread > 0 ? "0 0 20px rgba(245,158,11,0.06)" : "none" }}>
                <div className={`absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r ${totalUnread > 0 ? "from-amber-500/50" : "from-zinc-700/50"} to-transparent opacity-80`} />
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover/stat:opacity-100 transition-opacity duration-300" />
                <div className={`flex items-center gap-1.5 mb-1.5 relative ${totalUnread > 0 ? "text-amber-400/60" : "text-zinc-500"}`}>
                  <Inbox className="w-3.5 h-3.5" />
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-wider font-medium">Non lus</span>
                </div>
                <p className={`text-lg sm:text-2xl font-mono font-bold relative tracking-tight ${totalUnread > 0 ? "text-amber-400" : "text-zinc-600"}`}>{totalUnread}</p>
              </div>
              <div className="group/stat bg-zinc-900/40 backdrop-blur-sm border border-zinc-800/50 border-emerald-500/10 p-3 sm:p-4 rounded-xl relative overflow-hidden hover:border-zinc-700/50 transition-colors duration-150" style={{ boxShadow: "0 0 20px rgba(52,211,153,0.06)" }}>
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-emerald-500/50 to-transparent opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover/stat:opacity-100 transition-opacity duration-300" />
                <div className="flex items-center gap-1.5 text-emerald-400/60 mb-1.5 relative">
                  <Shield className="w-3.5 h-3.5" />
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-wider font-medium">Permanents</span>
                </div>
                <p className="text-lg sm:text-2xl font-mono font-bold text-emerald-400 relative tracking-tight">{permanentEmails}</p>
              </div>
            </div>

            {/* Create Email */}
            {profile?.permissions?.can_create_emails && (
              <div className="space-y-3">
                {showCreateEmail ? (
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
                    <h3 className="text-sm text-white font-medium">Créer un nouvel email</h3>
                    {profile?.permissions?.can_custom_address && (
                      <Input
                        placeholder="Adresse personnalisée (optionnel)"
                        value={newEmailAddress}
                        onChange={(e) => setNewEmailAddress(e.target.value)}
                        className="bg-zinc-900 border-zinc-700 text-white text-sm font-mono"
                      />
                    )}
                    {siteSettings?.durations && (() => {
                      const allOptions = [
                        { key: "", label: siteSettings.durations.find(d => d.key === siteSettings.default_duration)?.label || siteSettings.default_duration },
                        ...siteSettings.durations.filter((d, i, arr) => d.enabled !== false && d.key !== siteSettings.default_duration && arr.findIndex(x => x.key === d.key) === i),
                        ...(profile?.permissions?.can_permanent ? [{ key: "permanent", label: "Permanent" }] : [])
                      ];
                      const selected = allOptions.find(o => o.key === newEmailLifetime) || allOptions[0];
                      return (
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-zinc-400">Durée :</label>
                          <Popover open={durationOpen} onOpenChange={setDurationOpen}>
                            <PopoverTrigger asChild>
                              <button className="flex items-center gap-2 bg-zinc-900 border border-zinc-700 hover:border-cyan-500/50 text-white text-xs rounded-lg px-3 py-1.5 transition-colors group/dur">
                                <Clock className="w-3 h-3 text-cyan-400" />
                                <span className="font-mono">{selected.label}</span>
                                <ChevronDown className="w-3 h-3 text-zinc-500 group-hover/dur:text-cyan-400 transition-colors" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="bg-zinc-900 border-zinc-700 p-1 w-auto min-w-[160px]" align="start">
                              <div className="flex flex-col">
                                {allOptions.map((o) => (
                                  <button
                                    key={o.key}
                                    onClick={() => { setNewEmailLifetime(o.key); setDurationOpen(false); }}
                                    className={`text-left text-xs font-mono px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                                      newEmailLifetime === o.key
                                        ? "text-cyan-400 bg-cyan-500/10"
                                        : "text-zinc-300 hover:bg-cyan-500/10 hover:text-cyan-400"
                                    }`}
                                  >
                                    {o.key === "permanent" ? (
                                      <span className="text-amber-400">∞</span>
                                    ) : (
                                      <Clock className="w-3 h-3 text-zinc-600" />
                                    )}
                                    <span>{o.label}</span>
                                    {newEmailLifetime === o.key && <Check className="w-3 h-3 ml-auto text-cyan-400" />}
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      );
                    })()}
                    <div className="flex gap-2">
                      <Button
                        onClick={handleCreateEmail}
                        disabled={isCreatingEmail}
                        size="sm"
                        className="bg-cyan-500 hover:bg-cyan-600 text-black text-xs flex-1"
                      >
                        {isCreatingEmail ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                        Créer
                      </Button>
                      <Button
                        onClick={() => { setShowCreateEmail(false); setNewEmailAddress(""); }}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 text-xs"
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={() => setShowCreateEmail(true)}
                    className="w-full bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20 text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un email
                  </Button>
                )}
              </div>
            )}

            {/* Email Selection Toolbar */}
            {emails.length > 0 && !selectedEmail && (
              <div className="flex items-center justify-between">
                {emailSelectionMode ? (
                  <div className="flex items-center gap-2 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2">
                    <button onClick={selectAllEmails} className="flex items-center gap-2 text-zinc-400 hover:text-white p-0.5 flex-1 cursor-pointer" title={selectedEmailIds.size === emails.length ? "Tout désélectionner" : "Tout sélectionner"}>
                      {selectedEmailIds.size === emails.length ? <CheckSquare className="w-4 h-4 text-cyan-400" /> : <Square className="w-4 h-4" />}
                      <span className="text-xs text-zinc-400">
                        {selectedEmailIds.size} sélectionné{selectedEmailIds.size > 1 ? "s" : ""}
                      </span>
                    </button>
                    <Button
                      onClick={handleBulkDeleteEmails}
                      disabled={selectedEmailIds.size === 0}
                      size="sm"
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-xs h-7 px-2"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                    </Button>
                    <Button
                      onClick={exitEmailSelectionMode}
                      variant="ghost"
                      size="sm"
                      className="text-zinc-400 hover:text-white text-xs h-7 px-2"
                    >
                      <X className="w-3 h-3 mr-1" /> Annuler
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => setEmailSelectionMode(true)}
                    variant="ghost"
                    size="sm"
                    className="text-zinc-500 hover:text-white text-xs"
                  >
                    <CheckSquare className="w-3.5 h-3.5 mr-1.5" /> Sélectionner
                  </Button>
                )}
              </div>
            )}

            {/* Email List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
              </div>
            ) : emails.length === 0 ? (
              <div className="text-center py-16 space-y-3">
                <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mx-auto border border-zinc-800">
                  <Mail className="w-8 h-8 text-zinc-700" />
                </div>
                <p className="text-zinc-500 text-sm">Aucun email</p>
                <p className="text-zinc-600 text-xs">
                  {profile?.permissions?.can_create_emails
                    ? "Créez votre premier email avec le bouton ci-dessus"
                    : "Contactez un administrateur pour obtenir un email"}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {[...emails].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)).map((email) => {
                  const remaining = formatRemaining(email.remaining_seconds);
                  return (
                    <div
                      key={email.id}
                      onClick={emailSelectionMode ? () => toggleEmailSelection(email.id) : undefined}
                      className={`w-full text-left border rounded-xl p-4 transition-colors group ${
                        selectedEmailIds.has(email.id) ? "border-cyan-500/40 bg-cyan-500/5" :
                        email.is_expired ? "bg-zinc-900/30 border-red-500/20 opacity-50" :
                        email.is_permanent ? "bg-gradient-to-r from-emerald-500/[0.04] to-zinc-900/50 border-emerald-500/15 hover:border-emerald-500/30" :
                        email.pinned ? "bg-gradient-to-r from-amber-500/[0.04] to-zinc-900/50 border-amber-500/20 hover:border-amber-500/30" :
                        "bg-zinc-900/50 border-zinc-800 hover:border-cyan-500/30 hover:bg-zinc-900/80"
                      } ${emailSelectionMode ? "cursor-pointer" : ""}`}
                    >
                      <div className="flex items-center gap-3">
                        {emailSelectionMode ? (
                          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0">
                            {selectedEmailIds.has(email.id)
                              ? <CheckSquare className="w-5 h-5 text-cyan-400" />
                              : <Square className="w-5 h-5 text-zinc-600" />
                            }
                          </div>
                        ) : (
                        <div
                          onClick={() => openEmail(email)}
                          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 cursor-pointer ${
                            email.is_permanent ? "bg-emerald-500/15 border border-emerald-500/20" :
                            email.is_expired ? "bg-red-500/15 border border-red-500/20" :
                            "bg-cyan-500/15 border border-cyan-500/20"
                          }`}
                        >
                          <Mail className={`w-4 h-4 ${
                            email.is_permanent ? "text-emerald-400" :
                            email.is_expired ? "text-red-400" :
                            "text-cyan-400"
                          }`} />
                        </div>
                        )}
                        <div className="min-w-0 flex-1 cursor-pointer" onClick={emailSelectionMode ? undefined : () => openEmail(email)}>
                          <div className="flex items-center gap-2">
                            {email.pinned && <Pin className="w-3 h-3 text-amber-400 shrink-0" />}
                            <p
                              onClick={(e) => { e.stopPropagation(); copyAddress(email.full_address); }}
                              className="text-sm text-white font-mono truncate cursor-pointer hover:text-cyan-400 transition-colors"
                              title="Cliquer pour copier"
                            >{email.full_address}</p>
                            {email.unread_count > 0 && (
                              <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-[9px] px-1.5 py-0">
                                {email.unread_count} nouveau{email.unread_count > 1 ? "x" : ""}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              {email.message_count} message{email.message_count > 1 ? "s" : ""}
                            </span>
                            {remaining && (
                              <span className={`text-[11px] flex items-center gap-1 ${
                                email.is_expired ? "text-red-400" : "text-zinc-500"
                              }`}>
                                <Clock className="w-3 h-3" />
                                {remaining}
                              </span>
                            )}
                            {email.is_permanent && (
                              <span className="text-[11px] text-emerald-400 flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                Permanent
                              </span>
                            )}
                          </div>
                        </div>
                        {!emailSelectionMode && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); copyAddress(email.full_address); }}
                              className="p-1.5 rounded-md transition-colors shrink-0 text-zinc-700 hover:text-cyan-400 hover:bg-cyan-500/10 opacity-0 group-hover:opacity-100"
                              title="Copier l'adresse"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); togglePinEmail(email.id, email.pinned); }}
                              className={`p-1.5 rounded-md transition-colors shrink-0 ${
                                email.pinned
                                  ? "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                                  : "text-zinc-600 hover:text-amber-400 hover:bg-amber-500/10"
                              }`}
                              title={email.pinned ? "Désépingler" : "Épingler"}
                            >
                              <Pin className="w-4 h-4" />
                            </button>
                            <div className="cursor-pointer shrink-0" onClick={() => openEmail(email)}>
                              <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-cyan-400 transition-colors" />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : activeTab === "tickets" ? (
          /* ===== TICKETS TAB ===== */
          <div className="max-w-lg mx-auto">
            <UserTicketsSection ref={ticketsRef} token={token} />
          </div>
        ) : (
          /* ===== ACCOUNT TAB ===== */
          <div className="max-w-lg mx-auto space-y-6">
            {/* Profile & Password Section */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 sm:p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="relative group">
                  {renderAvatar("lg")}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  >
                    <Camera className="w-5 h-5 text-white" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </div>
                <div className="flex-1">
                  <h2 className="text-white font-medium">Mon compte</h2>
                  <p className="text-xs text-zinc-500">Gérez votre profil, mot de passe et sécurité</p>
                  <div className="flex gap-2 mt-1.5">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                      Changer l'avatar
                    </button>
                    {avatar && (
                      <button onClick={deleteAvatar} className="text-[10px] text-red-400 hover:text-red-300 transition-colors">
                        Supprimer
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-400 mb-1.5 block">Nom d'utilisateur</label>
                  <div className="px-3 py-2 bg-zinc-800/30 border border-zinc-700/50 rounded-md text-sm text-zinc-500 font-mono cursor-not-allowed select-none flex items-center gap-2">
                    <Lock className="w-3.5 h-3.5 text-zinc-600" />
                    {profile?.username || "..."}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-zinc-400 mb-1.5 block">Rôle</label>
                  <Badge className={`text-xs ${
                    profile?.role === "super_admin"
                      ? "bg-red-500/20 text-red-400 border-red-500/30"
                      : profile?.role === "admin"
                      ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                      : "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                  }`}>
                    {profile?.role === "super_admin" ? "Super Admin" : profile?.role === "admin" ? "Administrateur" : "Utilisateur"}
                  </Badge>
                </div>

                <hr className="border-zinc-800" />

                <div>
                  <label className="text-xs text-zinc-400 mb-1.5 block">
                    Mot de passe actuel <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 bg-zinc-900 border-zinc-700 text-white text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-zinc-400 mb-1.5 block">Nouveau mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Nouveau mot de passe"
                      className="pl-10 pr-10 bg-zinc-900 border-zinc-700 text-white text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {newPassword && (
                  <div>
                    <label className="text-xs text-zinc-400 mb-1.5 block">Confirmer le nouveau mot de passe</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirmer le mot de passe"
                        className={`pl-10 bg-zinc-900 border-zinc-700 text-white text-sm ${confirmPassword && confirmPassword !== newPassword ? "border-red-500" : ""}`}
                      />
                    </div>
                    {confirmPassword && confirmPassword !== newPassword && (
                      <p className="text-xs text-red-400 mt-1">Les mots de passe ne correspondent pas</p>
                    )}
                  </div>
                )}

                <Button
                  onClick={handleChangePassword}
                  disabled={isSaving || !currentPassword || !newPassword || (newPassword !== confirmPassword)}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-black text-sm mt-2"
                >
                  {isSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Sauvegarder
                </Button>
              </div>
            </div>

            {/* 2FA Section */}
            <UserTwoFASection token={token} />

            {/* Passkeys Section */}
            <UserPasskeysSection token={token} />
          </div>
        )}
      </main>

      {/* Message Modal */}
      <Dialog open={isMessageOpen} onOpenChange={(open) => { setIsMessageOpen(open); if (!open) setShowReply(false); }}>
        <DialogContent className="bg-zinc-950 border border-zinc-800 max-w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[85vh] overflow-hidden [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle className={`font-mono pr-8 text-base break-words ${selectedMessage?.direction === "sent" ? "text-emerald-400" : "text-cyan-400"}`}>
              {decodeMimeWords(selectedMessage?.subject) || "(sans sujet)"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 font-mono text-xs break-all">
              {selectedMessage?.direction === "sent" ? `→ ${selectedMessage?.recipient || selectedMessage?.sender}` : `De ${selectedMessage?.sender}`}
            </DialogDescription>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 py-3 border-b border-zinc-800">
                {selectedMessage.direction === "sent" ? (
                  <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0">
                    <Send className="w-4 h-4 text-emerald-400" />
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded-full bg-cyan-500/15 border border-cyan-500/20 flex items-center justify-center text-[10px] font-bold font-mono text-cyan-400 shrink-0">
                    {getInitials(selectedMessage.sender?.split("@")[0] || selectedMessage.sender)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${selectedMessage.direction === "sent" ? "text-emerald-400" : "text-white"}`}>
                    {selectedMessage.direction === "sent" ? `→ ${selectedMessage.recipient || selectedMessage.sender}` : senderLabel(selectedMessage)}
                  </p>
                  <p className="text-zinc-500 text-[10px] font-mono">
                    {new Date(selectedMessage.received_at).toLocaleString("fr-FR")} - {formatRelativeTime(selectedMessage.received_at)}
                  </p>
                </div>
                {selectedEmail?.reply_enabled && profile?.permissions?.can_reply && selectedMessage.direction !== "sent" && (
                  messages.some(m => m.direction === "sent" && m.replied_to === selectedMessage.id) ? (
                    <Button disabled size="sm" variant="outline" className="border-emerald-500/20 text-emerald-400/60 text-xs shrink-0 cursor-not-allowed">
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                      Répondu
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setReplyTo(selectedMessage.sender);
                        setReplySubject(`Re: ${selectedMessage.subject || ""}`);
                        setReplyBody("");
                        setShowReply(true);
                      }}
                      size="sm"
                      variant="outline"
                      className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 text-xs shrink-0"
                    >
                      <Reply className="w-3.5 h-3.5 mr-1.5" />
                      Répondre
                    </Button>
                  )
                )}
              </div>

              {showReply ? (
                <div className="space-y-3 border border-cyan-500/20 bg-cyan-500/5 rounded-lg p-3">
                  <div>
                    <label className="text-[10px] text-zinc-500 mb-1 block">À</label>
                    <Input
                      value={replyTo}
                      onChange={(e) => setReplyTo(e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-white text-sm h-8 font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 mb-1 block">Sujet</label>
                    <Input
                      value={replySubject}
                      onChange={(e) => setReplySubject(e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 mb-1 block">Message</label>
                    <textarea
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      rows={4}
                      className="w-full bg-zinc-900 border border-zinc-700 text-white text-sm rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
                      placeholder="Votre réponse..."
                      autoFocus
                    />
                  </div>
                  {/* Attachment UI */}
                  <div>
                    <input type="file" ref={replyFileRef} multiple className="hidden" onChange={(e) => setReplyFiles(prev => [...prev, ...Array.from(e.target.files)].slice(0, 5))} />
                    <button
                      type="button"
                      onClick={() => replyFileRef.current?.click()}
                      className="flex items-center gap-1.5 text-[10px] text-zinc-500 hover:text-cyan-400 transition-colors"
                    >
                      <Paperclip className="w-3 h-3" />
                      Joindre un fichier ({replyFiles.length}/5)
                    </button>
                    {replyFiles.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {replyFiles.map((f, i) => (
                          <span key={i} className="inline-flex items-center gap-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-0.5 text-[10px] text-zinc-300">
                            <Paperclip className="w-2.5 h-2.5 text-cyan-400" />
                            {f.name.length > 20 ? f.name.slice(0, 17) + "..." : f.name}
                            <button onClick={() => setReplyFiles(prev => prev.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400 ml-0.5">
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleReply}
                      disabled={isSendingReply || !replyTo.trim() || !replySubject.trim() || !replyBody.trim()}
                      size="sm"
                      className="bg-cyan-500 hover:bg-cyan-600 text-black text-xs flex-1"
                    >
                      {isSendingReply ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                      Envoyer{replyFiles.length > 0 ? ` (${replyFiles.length} fichier${replyFiles.length > 1 ? "s" : ""})` : ""}
                    </Button>
                    <Button
                      onClick={() => { setShowReply(false); setReplyFiles([]); }}
                      variant="ghost"
                      size="sm"
                      className="text-zinc-400 text-xs"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <ScrollArea className="h-[350px] [&>[data-radix-scroll-area-viewport]>div]:!block">
                  {selectedMessage.body_html ? (
                    <EmailHtmlView html={selectedMessage.body_html} />
                  ) : (
                    <pre className="whitespace-pre-wrap break-words text-zinc-300 font-mono text-sm leading-relaxed">
                      {selectedMessage.body}
                    </pre>
                  )}
                  {selectedMessage.attachments?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-zinc-800">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] text-zinc-500 font-mono uppercase flex items-center gap-1">
                          <Paperclip className="w-3 h-3" /> {selectedMessage.attachments.length} pièce{selectedMessage.attachments.length > 1 ? "s" : ""} jointe{selectedMessage.attachments.length > 1 ? "s" : ""}
                        </p>
                        {selectedMessage.attachments.length > 1 && (
                          <button
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API}/messages/${selectedMessage.id}/attachments/download-all`);
                                if (!res.ok) {
                                  for (const att of selectedMessage.attachments) {
                                    try {
                                      const r = await fetch(`${API}/attachments/${att.id}`);
                                      const b = await r.blob();
                                      const u = URL.createObjectURL(b);
                                      const a = document.createElement("a");
                                      a.href = u; a.download = att.filename || "download";
                                      document.body.appendChild(a); a.click(); a.remove();
                                      URL.revokeObjectURL(u);
                                    } catch { }
                                    await new Promise(r => setTimeout(r, 300));
                                  }
                                  return;
                                }
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `pieces-jointes.zip`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                URL.revokeObjectURL(url);
                              } catch { }
                            }}
                            className="inline-flex items-center gap-1 text-[10px] font-mono text-cyan-400 hover:text-cyan-300 transition-colors"
                          >
                            <Download className="w-3 h-3" />
                            Tout télécharger
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedMessage.attachments.map((att) => (
                          <button
                            key={att.id}
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API}/attachments/${att.id}`);
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = att.filename || "download";
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                URL.revokeObjectURL(url);
                              } catch { }
                            }}
                            className="inline-flex items-center gap-1.5 bg-zinc-800/80 border border-zinc-700 hover:border-cyan-500/30 hover:bg-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-300 hover:text-cyan-400 transition-colors group"
                          >
                            {att.content_type?.startsWith("image/") ? (
                              <Camera className="w-3.5 h-3.5 text-emerald-400" />
                            ) : (
                              <Paperclip className="w-3.5 h-3.5 text-cyan-400" />
                            )}
                            <span className="max-w-[150px] truncate">{att.filename}</span>
                            <span className="text-zinc-600 text-[9px]">{att.size > 1024 * 1024 ? `${(att.size / 1024 / 1024).toFixed(1)}MB` : `${Math.round(att.size / 1024)}KB`}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </ScrollArea>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Account Disabled Dialog */}
      <Dialog open={showDisabledDialog} onOpenChange={() => {}}>
        <DialogContent className="bg-zinc-900 border-red-500/30 max-w-sm [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <div className="flex justify-center mb-3">
              <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                <Shield className="w-7 h-7 text-red-400" />
              </div>
            </div>
            <DialogTitle className="text-center text-white text-lg">Compte désactivé</DialogTitle>
            <DialogDescription className="text-center text-zinc-400 mt-2">
              {disabledReason}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              onClick={handleDisabledLogout}
              className="bg-red-500 hover:bg-red-600 text-white px-6"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Se déconnecter
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
